import type { ChainId } from '@morpho-org/blue-sdk'
import type { FilterItem } from '@morpho-org/ui-v6/client'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils'

import type { AssetItem } from '@/components/common/TableAdvancedFilters/AssetFilter'
import type { CuratorItem } from '@/components/common/TableAdvancedFilters/CuratorFilter'
import type { UserPositionAssetFacet } from '@/lib/procedures/userPositions.contract'
import type { VaultListFacets } from '@/lib/procedures/vaultList.contract'

/** `user.*Positions` asset-facet metadata (camelCase wire). */
interface PositionAssetFacetMetadata {
  chainId: number
  address: `0x${string}`
  symbol: string
  logoURI: string | null
  tags: string[] | null
}

interface GetFacetFiltersParams {
  facets: VaultListFacets | undefined
}

type TokenFacet = VaultListFacets['collateralExposure'][number]
type DescribedTokenFacet = TokenFacet & { metadata: NonNullable<TokenFacet['metadata']> }

/** A token facet without metadata has nothing to render, so the filters skip it. */
const describedTokenFacets = (facets: TokenFacet[]): DescribedTokenFacet[] =>
  facets.filter((facet): facet is DescribedTokenFacet => facet.metadata !== null)

const tokenFacetToAssetItem = (facet: DescribedTokenFacet): AssetItem => ({
  id: facet.value,
  address: facet.metadata.address,
  symbol: facet.metadata.symbol,
  tags: facet.metadata.tags,
  logoURI: facet.metadata.logoUri,
  chain: {
    id: facet.metadata.chainId,
    network: InterfaceChainUtils.getIdentifierById(facet.metadata.chainId)
  },
  count: facet.count
})

export function assetsToFilterItems(
  assets: AssetItem[],
  selectedValues: string[],
  options?: { showChain?: boolean }
): FilterItem<string>[] {
  // Note: counts are intentionally NOT forwarded here. This helper is only used
  // as the collateral exposure/exclusion fallback when collateral facets are
  // missing, where the source `AssetItem.count` holds deposit-asset facet totals
  // that do not describe the collateral option being toggled. Genuine collateral
  // counts flow through `collateralAssetsToFilterItems` instead.
  return assets.map(asset => ({
    id: asset.id,
    value: asset.id,
    label: asset.symbol,
    isChecked: selectedValues.includes(asset.id),
    img: asset.logoURI,
    ...(options?.showChain && {
      chain: {
        id: asset.chain.id,
        name: asset.chain.network ?? InterfaceChainUtils.getIdentifierById(asset.chain.id)
      }
    }),
    searchableFields: [asset.address]
  }))
}

export function curatorsToFilterItems(
  curators: CuratorItem[],
  selectedValues: string[]
): FilterItem<string>[] {
  return curators.map(curator => ({
    id: curator.id,
    value: curator.id,
    label: curator.name,
    isChecked: selectedValues.includes(curator.id),
    img: curator.image,
    count: curator.count
  }))
}

const positionFacetMetadataToAssetItem = (metadata: PositionAssetFacetMetadata): AssetItem => ({
  id: `${metadata.chainId}:${metadata.address.toLowerCase()}`,
  address: metadata.address,
  symbol: metadata.symbol,
  tags: metadata.tags,
  logoURI: metadata.logoURI,
  chain: {
    id: metadata.chainId,
    network: InterfaceChainUtils.getIdentifierById(metadata.chainId)
  }
})

/** `user.*Positions` asset facets (`logoUri`, nullable `symbol`) as filter items. */
export function userPositionFacetsToAssetItems(
  facets: UserPositionAssetFacet[] | undefined
): AssetItem[] {
  if (!facets) return []

  return facets.map(({ metadata: { logoUri, symbol, ...metadata } }) =>
    positionFacetMetadataToAssetItem({
      ...metadata,
      symbol: symbol ?? UNKNOWN_SYMBOL,
      logoURI: logoUri
    })
  )
}

export function collateralFacetsToAssetItems(facets: DescribedTokenFacet[]): AssetItem[] {
  return facets.map(tokenFacetToAssetItem)
}

export function collateralAssetsToFilterItems(
  facets: DescribedTokenFacet[],
  selectedValues: string[],
  options?: { showChain?: boolean }
): FilterItem<string>[] {
  return facets.map(facet => ({
    id: facet.value,
    value: facet.value,
    label: facet.metadata.symbol,
    isChecked: selectedValues.includes(facet.value),
    img: facet.metadata.logoUri,
    ...(options?.showChain && {
      chain: {
        id: facet.metadata.chainId,
        name: InterfaceChainUtils.getIdentifierById(facet.metadata.chainId)
      }
    }),
    searchableFields: [facet.metadata.address],
    count: facet.count
  }))
}

export const getFacetFilters = ({ facets }: GetFacetFiltersParams) => {
  if (!facets) {
    return {
      filteredAssets: [],
      filteredCurators: [],
      filteredChainIds: [],
      // undefined (not an empty Map) while facets are still loading, so the network
      // filter can tell "not loaded yet" from "loaded with no matching chains" and
      // render 0 rather than a blank in the latter, all-zero case.
      filteredChainCounts: undefined,
      filteredChainTvls: undefined,
      filteredCollateralAssets: [],
      filteredCollateralExclusionAssets: []
    }
  }

  const filteredAssets = describedTokenFacets(facets.assets).map(tokenFacetToAssetItem)

  const filteredCurators: CuratorItem[] = facets.curatorIds.flatMap(facet =>
    facet.metadata
      ? [
          {
            id: facet.value,
            name: facet.metadata.name,
            image: facet.metadata.image,
            count: facet.count
          }
        ]
      : []
  )

  const filteredChainIds = facets.chainIds.map(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    facet => facet.value as ChainId
  )

  const filteredChainCounts = new Map<ChainId, number>(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    facets.chainIds.map(facet => [facet.value as ChainId, facet.count])
  )

  // Absolute per-chain TVL (USD) used to sort the network filter by chain size.
  const filteredChainTvls = new Map<ChainId, number>(
    // oxlint-disable-next-line typescript/consistent-type-assertions
    facets.chainIds.map(facet => [facet.value as ChainId, facet.totalAssetsUsd])
  )

  const filteredCollateralAssets = describedTokenFacets(facets.collateralExposure)
  const filteredCollateralExclusionAssets = describedTokenFacets(facets.collateralExclusion)

  return {
    filteredAssets,
    filteredCurators,
    filteredChainIds,
    filteredChainCounts,
    filteredChainTvls,
    filteredCollateralAssets,
    filteredCollateralExclusionAssets
  }
}
